import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Silhouette } from "../components/Silhouette";
import { createJourney } from "../lib/api";
import { updateState } from "../lib/storage";
import { ArrowRight, ChevronLeft } from "lucide-react";

const AVATARS = [
  { id: "cosmic", color: "#00F0FF", label: "Cosmic Cyan" },
  { id: "nebula", color: "#B026FF", label: "Nebula Violet" },
  { id: "aurora", color: "#FFD700", label: "Aurora Gold" },
];

export default function Create() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    age: "",
    income: "",
    career: "",
    relationship: "",
    children: "0",
    goals: "",
    dreams: "",
    avatar_style: "cosmic",
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const begin = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        children: form.children ? parseInt(form.children, 10) : 0,
      };
      const j = await createJourney(payload);
      updateState({ journeyId: j.id, character: j.character });
      toast.success("Your journey begins.");
      navigate(`/journey/${j.id}`);
    } catch (e) {
      toast.error("Something glitched in the simulation. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedColor = AVATARS.find((a) => a.id === form.avatar_style)?.color;

  return (
    <div className="min-h-screen px-6 py-10 max-w-6xl mx-auto">
      <button onClick={() => navigate("/")} className="btn-pill mb-6" data-testid="back-btn">
        <ChevronLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-5 gap-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="md:col-span-2 flex flex-col items-center md:items-start"
        >
          <div className="font-mono-label mb-3 text-white/50">Your starting self</div>
          <h1 className="text-3xl md:text-4xl font-display font-light leading-tight mb-6">
            Shape the <span className="text-glow-cyan">you</span> of today.
          </h1>
          <div className="float-y my-4">
            <Silhouette size={180} color={selectedColor} />
          </div>
          <div className="flex gap-3 mt-4">
            {AVATARS.map((a) => (
              <button
                key={a.id}
                onClick={() => setForm({ ...form, avatar_style: a.id })}
                data-testid={`avatar-${a.id}`}
                className="btn-pill text-xs"
                style={{
                  borderColor: form.avatar_style === a.id ? a.color : "rgba(255,255,255,0.1)",
                  color: form.avatar_style === a.id ? a.color : "white",
                  boxShadow: form.avatar_style === a.id ? `0 0 18px ${a.color}66` : "none",
                }}
              >
                <span className="w-3 h-3 rounded-full" style={{ background: a.color, boxShadow: `0 0 10px ${a.color}` }} />
                {a.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-white/40 mt-8 max-w-sm">
            Everything is optional. The more you share, the more vivid your rabbit hole becomes.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="md:col-span-3 glass rounded-3xl p-8 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Name" value={form.name} onChange={upd("name")} testId="field-name" placeholder="Alex" />
            <Field label="Age" value={form.age} onChange={upd("age")} testId="field-age" placeholder="29" type="number" />
            <Field label="Current income" value={form.income} onChange={upd("income")} testId="field-income" placeholder="$68k / year" />
            <Field label="Career" value={form.career} onChange={upd("career")} testId="field-career" placeholder="Product designer" />
            <Field label="Relationship" value={form.relationship} onChange={upd("relationship")} testId="field-rel" placeholder="Single / dating / married" />
            <Field label="Children" value={form.children} onChange={upd("children")} testId="field-kids" placeholder="0" type="number" />
          </div>
          <div className="mt-5">
            <Field label="Goals" value={form.goals} onChange={upd("goals")} testId="field-goals" placeholder="Buy a home, start a company…" textarea />
          </div>
          <div className="mt-5">
            <Field label="Dreams" value={form.dreams} onChange={upd("dreams")} testId="field-dreams" placeholder="A quiet studio in Lisbon, a thriving brand…" textarea />
          </div>
          <button
            onClick={begin}
            disabled={loading}
            className="btn-pill btn-pill-primary mt-8 w-full justify-center text-base"
            data-testid="begin-journey-btn"
          >
            {loading ? "Opening the simulation…" : (<>Enter the rabbit hole <ArrowRight size={16} /></>)}
          </button>
          <p className="text-[11px] text-white/35 mt-4 text-center">
            All outcomes you'll see are imagined possibilities, not predictions. Your data stays on this device unless you choose to save it.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder, type = "text", textarea, testId }) => (
  <label className="block">
    <span className="font-mono-label block mb-2">{label}</span>
    {textarea ? (
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        rows={3}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_16px_rgba(0,240,255,0.25)] transition-colors"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_16px_rgba(0,240,255,0.25)] transition-colors"
      />
    )}
  </label>
);
